import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";

export const RenamePinDialog = ({
  open,
  onClose,
  onConfirm,
  currentPinName,
  existingPinNames,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (newPinName: string) => void;
  currentPinName: string;
  existingPinNames: string[];
}) => {
  const [pinName, setPinName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setPinName(currentPinName);
      setError("");
    }
  }, [open, currentPinName]);

  const validatePinName = (nameToValidate?: string): string | null => {
    const name = nameToValidate ?? pinName;
    const trimmedName = name.trim();

    if (!trimmedName) {
      setError("Pin name is required");
      return null;
    }

    // Check if the new name is different from current
    if (trimmedName === currentPinName) {
      setError("");
      return null; // Will be handled by caller
    }

    // Check if name already exists (excluding current pin) - case-insensitive
    const otherPinNames = existingPinNames.filter(
      (name) => name !== currentPinName
    );
    const normalizedExisting = otherPinNames.map((n) => n.toLowerCase().trim());
    if (normalizedExisting.includes(trimmedName.toLowerCase())) {
      setError("A pin with this name already exists");
      return null;
    }

    if (trimmedName.length > 50) {
      setError("Pin name must be 50 characters or less");
      return null;
    }

    // Validation passed - clear any existing error
    setError("");
    return trimmedName;
  };

  const handleConfirm = () => {
    const trimmedName = validatePinName();
    if (!trimmedName) {
      // If validation returned null, check if it's because name hasn't changed
      if (pinName.trim() === currentPinName) {
        onClose();
      }
      return;
    }

    onConfirm(trimmedName);
    setPinName("");
    setError("");
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleConfirm();
    }
  };

  const theme = useTheme();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>Rename Pin</Box>
        <IconButton
          onClick={onClose}
          sx={{
            p: 0,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box>
          <Typography variant="body1" gutterBottom>
            Enter a new name for this pin:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            value={pinName}
            variant="standard"
            sx={{ mt: -0.5 }}
            onChange={(e) => {
              const newValue = e.target.value;
              setPinName(newValue);
              // Validate on change to show errors in real-time
              if (newValue.trim() && newValue.trim() !== currentPinName) {
                validatePinName(newValue);
              } else {
                setError("");
              }
              // Note: validatePinName will clear error if validation passes
            }}
            onKeyPress={handleKeyPress}
            error={!!error}
            helperText={error || "Enter a descriptive name for this pin"}
            margin="normal"
            placeholder="e.g., Q1 Analysis, Holiday Sales, Default View"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={
            !pinName.trim() || pinName.trim() === currentPinName || !!error
          }
        >
          Rename Pin
        </Button>
      </DialogActions>
    </Dialog>
  );
};
